import React from 'react';

// ─── Shared, original NPS face set ───────────────────────────────────────
// One consistent expression per band (approved design):
//   0–6  Detractor — frown   · colour: red
//   7–8  Passive   — flat    · colour: amber
//   9–10 Promoter  — smile   · colour: green
// Black & White keeps the identical expressions on a light face with a dark
// outline + dark features (no colour). No emoji, no third-party art.
// Reused across builder previews, respondent surveys, reports and PDFs.

export const NPS_BAND_COLORS = {
  detractor: '#EF4444',
  passive: '#F59E0B',
  promoter: '#22C55E',
};

export function npsLevel(score) {
  const s = Number(score);
  if (s >= 9) return 'promoter';
  if (s >= 7) return 'passive';
  return 'detractor';
}

export function npsClassification(score) {
  if (score == null || score === '') return null;
  const lvl = npsLevel(score);
  return lvl === 'promoter' ? 'Promoter' : lvl === 'passive' ? 'Passive' : 'Detractor';
}

// Resolve any stored (incl. legacy) NPS display config to {style, appearance}.
// Mirrors backend services/nps_presentation.resolve_nps_presentation.
export function resolveNpsPresentation(question) {
  const raw = question?.nps_display_style;
  let appearance = question?.nps_face_appearance;
  let style;
  if (raw === 'numbers') style = 'numbers';
  else if (raw === 'faces') style = 'faces';
  else if (raw === 'faces_numbers') style = 'faces_numbers';
  else if (raw === 'faces_neutral') { style = 'faces_numbers'; appearance = appearance || 'bw'; }
  else style = 'faces_numbers';
  return { style, appearance: appearance || 'color' };
}

const MOUTHS = {
  detractor: 'M14.5 33 Q24 25.5 33.5 33',
  passive: 'M15 30.5 L33 30.5',
  promoter: 'M14 28 Q24 37.5 34 28',
};

// Pure, non-interactive face. `size` accepts any CSS length (px string, clamp()).
export function NpsFace({ score, appearance = 'color', size = 40, level: levelProp, className, style, title }) {
  const level = levelProp || npsLevel(score);
  const mouth = MOUTHS[level];
  const cssSize = typeof size === 'number' ? `${size}px` : size;
  const isBw = appearance === 'bw';
  const circleFill = isBw ? '#F3F4F6' : NPS_BAND_COLORS[level];
  const features = isBw ? '#374151' : '#FFFFFF';
  return (
    <svg
      viewBox="0 0 48 48"
      width={cssSize}
      height={cssSize}
      className={className}
      style={style}
      role="img"
      aria-hidden={title ? undefined : true}
      aria-label={title}
    >
      {title ? <title>{title}</title> : null}
      <circle cx="24" cy="24" r={isBw ? 21.25 : 22} fill={circleFill}
        stroke={isBw ? '#374151' : 'none'} strokeWidth={isBw ? 1.5 : 0} />
      <circle cx="16.5" cy="20" r="2.6" fill={features} />
      <circle cx="31.5" cy="20" r="2.6" fill={features} />
      <path d={mouth} fill="none" stroke={features} strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

export default NpsFace;
